import React, { useState, useEffect } from "react";
import { X } from "lucide-react";

// Opens a base64 data: URL (or any URL) safely in a modal. Browsers block
// top-level navigation to data: URLs, so we convert to a Blob URL for viewing.
function DocModal({ url, label, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [kind, setKind] = useState("loading"); // loading | image | pdf | other | error

  useEffect(() => {
    let created = null;
    (async () => {
      try {
        const res = await fetch(url);
        const blob = await res.blob();
        created = URL.createObjectURL(blob);
        setBlobUrl(created);
        setKind(blob.type === "application/pdf" ? "pdf" : blob.type.startsWith("image/") ? "image" : "other");
      } catch (e) { setKind("error"); }
    })();
    return () => { if (created) URL.revokeObjectURL(created); };
  }, [url]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="doc-viewer-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-black/10 sticky top-0 bg-white">
          <h3 className="font-display text-lg text-[#1f1a1b]">{label}</h3>
          <div className="flex items-center gap-2">
            {blobUrl && <a href={blobUrl} target="_blank" rel="noreferrer" data-testid="doc-open-new-tab" className="text-[13px] text-[#7c2233] border border-[#7c2233]/30 rounded-lg px-3 py-1.5 hover:bg-[#faf1f2]">Open in new tab</a>}
            <button onClick={onClose} aria-label="Close" data-testid="doc-close" className="p-1.5 rounded-lg hover:bg-black/5"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="p-4">
          {kind === "loading" && <p className="text-[14px] text-[#8a8285]">Loading…</p>}
          {kind === "error" && <p className="text-[14px] text-red-600">Could not load this document.</p>}
          {kind === "image" && <img src={blobUrl} alt={label} className="max-w-full mx-auto rounded-lg" />}
          {kind === "pdf" && <iframe title={label} src={blobUrl} className="w-full h-[72vh] rounded-lg border border-black/10" />}
          {kind === "other" && blobUrl && <a href={blobUrl} download className="text-[#7c2233] underline">Download file</a>}
        </div>
      </div>
    </div>
  );
}

export function DocButton({ url, label, icon: Icon, className, testid }) {
  const [open, setOpen] = useState(false);
  if (!url) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={testid} className={className}>
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </button>
      {open && <DocModal url={url} label={label} onClose={() => setOpen(false)} />}
    </>
  );
}

export default DocButton;
